import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, X, Trash2, Loader2, Menu } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HotspotEditorPage = () => {
  const { panoramaId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader, hasRole } = useAuth();
  const viewerRef = useRef(null);
  
  const [panorama, setPanorama] = useState(null);
  const [hotspots, setHotspots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [points, setPoints] = useState([]);
  const [polygonClosed, setPolygonClosed] = useState(false);
  const [zoneName, setZoneName] = useState('');
  const [zonePoints, setZonePoints] = useState(10);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!hasRole(['admin', 'editor'])) {
      toast.error('Доступ запрещён');
      navigate('/');
    }
  }, [hasRole, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [panoRes, hotspotsRes] = await Promise.all([
          axios.get(`${API}/panoramas/${panoramaId}`, { headers: getAuthHeader() }),
          axios.get(`${API}/panoramas/${panoramaId}/hotspots`)
        ]);
        setPanorama(panoRes.data);
        setHotspots(hotspotsRes.data || []);
      } catch (error) {
        toast.error('Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [panoramaId, getAuthHeader]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'krpano_click') {
        if (polygonClosed) return;
        const { ath, atv } = event.data;
        if (ath == null || atv == null || isNaN(ath) || isNaN(atv)) return;
        setPoints(prev => [...prev, { ath, atv }]);
      }
      if (event.data && event.data.type === 'polygon_closed') {
        setPolygonClosed(true);
        setMenuOpen(true);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [polygonClosed, points]);

  useEffect(() => {
    if (!panorama || !viewerRef.current) return;
    const viewerUrl = `${API}/panoramas/${panorama.id}/files/${panorama.html_file}?editor=true`;
    const iframe = document.createElement('iframe');
    iframe.src = viewerUrl;
    iframe.style.cssText = 'width:100%;height:100%;border:none;';
    viewerRef.current.innerHTML = '';
    viewerRef.current.appendChild(iframe);
  }, [panorama]);

  const clearMarkers = () => {
    const iframe = viewerRef.current?.querySelector('iframe');
    if (iframe) iframe.contentWindow.postMessage({type: 'clear_markers'}, '*');
  };

  const cancelDrawing = () => {
    setPoints([]);
    setPolygonClosed(false);
    setZoneName('');
    setZonePoints(10);
    setMenuOpen(false);
    clearMarkers();
  };

  const closePolygon = () => {
    if (points.length >= 3) {
      const iframe = viewerRef.current?.querySelector('iframe');
      if (iframe) iframe.contentWindow.postMessage({type: 'close_polygon'}, '*');
      setPolygonClosed(true);
      setMenuOpen(true);
    }
  };

  const removeLastPoint = () => {
    if (points.length > 0) {
      setPoints(prev => prev.slice(0, -1));
      const iframe = viewerRef.current?.querySelector('iframe');
      if (iframe) iframe.contentWindow.postMessage({type: 'remove_last_point'}, '*');
    }
  };

  const saveZone = async () => {
    if (points.length < 3) { toast.error('Нужно минимум 3 точки'); return; }
    if (!zoneName.trim()) { toast.error('Введите название'); return; }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/panoramas/${panoramaId}/hotspots`,
        { name: zoneName.trim(), points, reward_points: zonePoints },
        { headers: getAuthHeader() }
      );
      setHotspots(prev => [...prev, res.data.hotspot]);
      const iframe = viewerRef.current?.querySelector('iframe');
      if (iframe) iframe.contentWindow.postMessage({type: 'refresh_hotspots'}, '*');
      setPoints([]);
      setPolygonClosed(false);
      setZoneName('');
      setZonePoints(10);
      setMenuOpen(false);
      clearMarkers();
      toast.success('Зона сохранена!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const deleteZone = async (id) => {
    try {
      const response = await axios.delete(`${API}/panoramas/${panoramaId}/hotspots/${id}`, { headers: getAuthHeader() });
      setHotspots(prev => prev.filter(h => h.id !== id));
      const data = response.data;
      toast.success(`Зона "${data.hotspot_name || ''}" удалена`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка удаления');
    }
    const iframe = viewerRef.current?.querySelector("iframe");
    if (iframe) iframe.contentWindow.postMessage({type: "refresh_hotspots"}, "*");
  };

  const startEditing = (hotspot) => {
    setEditingId(hotspot.id);
    setEditingName(hotspot.name);
  };

  const saveEdit = async (id) => {
    if (!editingName.trim()) { toast.error('Введите название'); return; }
    try {
      await axios.put(
        `${API}/panoramas/${panoramaId}/hotspots/${id}`,
        { name: editingName.trim() },
        { headers: getAuthHeader() }
      );
      setHotspots(prev => prev.map(h => h.id === id ? { ...h, name: editingName.trim() } : h));
      setEditingId(null);
      setEditingName('');
      toast.success('Обновлено');
    } catch { toast.error('Ошибка'); }
  };

  const cancelEdit = () => { setEditingId(null); setEditingName(''); };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <Loader2 className="animate-spin text-purple-500" size={48} />
      </div>
    );
  }

  const isDrawing = points.length > 0 && !polygonClosed;

  const iconBtnStyle = {
    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    color: '#92400e',
    width: '40px',
    height: '40px',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    border: '1px solid #f59e0b',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s'
  };

  const badgeStyle = {
    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    color: '#92400e',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '0.9rem',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    border: '1px solid #f59e0b'
  };

  return (
    <div className="flex flex-col h-screen bg-gray-900">
      <div className="flex-1 relative">
        <div style={{ position: 'absolute', top: '16px', left: '16px', zIndex: 1002, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button onClick={() => navigate(-1)} style={iconBtnStyle}>
            <ArrowLeft size={18} />
          </button>
          <button onClick={() => setMenuOpen(!menuOpen)} style={{...iconBtnStyle, boxShadow: menuOpen ? '0 4px 12px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.15)'}}>
            <Menu size={18} />
          </button>
          {points.length > 0 && (
            <div style={badgeStyle}>
              {points.length} точек
            </div>
          )}
        </div>

        <div ref={viewerRef} className="w-full h-full" />

        {menuOpen && (
          <div style={{ position: 'absolute', top: '70px', left: '16px', zIndex: 1003, width: '288px', background: '#fff', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
            <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '12px' }}>
              {(points.length > 0 || polygonClosed) && (
                <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '12px', marginBottom: '12px', border: '1px solid #e9d5ff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontWeight: '600', fontSize: '14px' }}>Новая зона</span>
                    {points.length > 0 && !polygonClosed && (
                      <button onClick={removeLastPoint} style={{ color: '#ef4444', fontSize: '12px', background: 'none', border: 'none', cursor: 'pointer' }}>Удалить точку</button>
                    )}
                  </div>

                  {isDrawing && points.length >= 3 && (
                    <Button onClick={closePolygon} size="sm" className="w-full mb-2 bg-green-600 hover:bg-green-700">
                      Замкнуть полигон
                    </Button>
                  )}

                  {polygonClosed && (
                    <>
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Что увидит игрок:</label>
                        <Input value={zoneName} onChange={e => setZoneName(e.target.value)} placeholder="Статуя льва" className="h-9 text-sm" autoFocus />
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Баллы за находку:</label>
                        <Input type="number" value={zonePoints} onChange={e => setZonePoints(parseInt(e.target.value) || 0)} className="h-9 text-sm" />
                      </div>
                      <Button onClick={saveZone} disabled={saving} size="sm" className="w-full bg-green-600 hover:bg-green-700">
                        {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : <Save size={14} className="mr-1" />}
                        Сохранить
                      </Button>
                    </>
                  )}
                  <Button variant="outline" onClick={cancelDrawing} size="sm" className="w-full mt-2">
                    <X size={14} className="mr-1" /> Отмена
                  </Button>
                </div>
              )}

              {hotspots.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {hotspots.map(h => (
                    <div key={h.id} style={{ background: '#f9fafb', borderRadius: '8px', padding: '8px' }}>
                      {editingId === h.id ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <Input value={editingName} onChange={e => setEditingName(e.target.value)} className="h-8 text-sm" autoFocus />
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <Button size="sm" onClick={() => saveEdit(h.id)} className="flex-1 h-7 text-xs bg-green-600 hover:bg-green-700">
                              <Save size={12} className="mr-1" /> Сохранить
                            </Button>
                            <Button size="sm" variant="outline" onClick={cancelEdit} className="h-7 px-2">
                              <X size={12} />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ flex: 1, cursor: 'pointer', padding: '4px', margin: '-4px', borderRadius: '4px' }} onClick={() => startEditing(h)}>
                            <p style={{ fontWeight: '500', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.name}</p>
                            <p style={{ fontSize: '12px', color: '#6b7280' }}>{h.points?.length} точек • {h.reward_points} б.</p>
                          </div>
                          <button onClick={() => deleteZone(h.id)} style={{ color: '#ef4444', padding: '4px', marginLeft: '4px', background: 'none', border: 'none', cursor: 'pointer' }}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {hotspots.length === 0 && points.length === 0 && !polygonClosed && (
                <p style={{ color: '#9ca3af', fontSize: '12px', textAlign: 'center', padding: '12px 0' }}>Нет сохранённых зон</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HotspotEditorPage;
